/**
 * Footer band height for PDF letterheads.
 *
 * PDF footers sit in a fixed 66px band. A footer image wider in proportion than that band is
 * shrunk to fit and ends up narrower than the page. SVG footers stay sharp at any size, so for
 * those the band is sized to the image's proportions and the footer spans the full content width.
 * Raster footers (PNG/JPG) keep the fixed band so they are never scaled past their artwork.
 */

export const DEFAULT_FOOTER_HEIGHT_PX = 66;
/** A4 width at 96dpi minus the 36px side margins used by every PDF export. */
export const PDF_CONTENT_WIDTH_PX = 794 - 36 * 2;
const MIN_FOOTER_HEIGHT_PX = 24;
const MAX_FOOTER_HEIGHT_PX = 140;

export function isVectorImagePath(path: string | null | undefined): boolean {
  return /\.svg(?:[?#].*)?$/i.test(String(path || '').trim());
}

/** Footer band height for an image with the given width ÷ height. */
export function footerHeightForAspect(aspect: number | null | undefined): number {
  if (!aspect || !Number.isFinite(aspect) || aspect <= 0) return DEFAULT_FOOTER_HEIGHT_PX;
  const height = Math.round(PDF_CONTENT_WIDTH_PX / aspect);
  return Math.min(MAX_FOOTER_HEIGHT_PX, Math.max(MIN_FOOTER_HEIGHT_PX, height));
}

/** Width ÷ height of an image, or null if it cannot be loaded (or there is no browser). */
export function loadImageAspect(src: string | null | undefined): Promise<number | null> {
  const url = String(src || '').trim();
  if (!url || typeof Image === 'undefined') return Promise.resolve(null);
  return new Promise((resolve) => {
    let settled = false;
    const done = (value: number | null) => {
      if (settled) return;
      settled = true;
      resolve(value);
    };
    const img = new Image();
    img.onload = () => done(img.naturalWidth > 0 && img.naturalHeight > 0 ? img.naturalWidth / img.naturalHeight : null);
    img.onerror = () => done(null);
    setTimeout(() => done(null), 5000);
    img.src = url;
  });
}

/** Footer band height for a PDF: sized to the image for SVG footers, the fixed band otherwise. */
export async function pdfFooterHeightPx(footerPath: string | null | undefined): Promise<number> {
  if (!isVectorImagePath(footerPath)) return DEFAULT_FOOTER_HEIGHT_PX;
  return footerHeightForAspect(await loadImageAspect(footerPath));
}
